import React, { useState, useMemo, useEffect, useRef } from "react";
import { Tabs, Tab, Box, Typography, Button, Chip, CircularProgress, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, IconButton, TextField, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Stack } from "@mui/material";
import { Save as SaveIcon, Edit as EditIcon, Delete as DeleteIcon, Close as CloseIcon } from "@mui/icons-material";
import StyledDrawer from "../../shared/UIParts/StyledDrawer";
import { ManualMimshakData, ManualProductType } from "../../interfaces/DTO/manualMimshakData";
import { formatCurrency, formatFullName } from "../../utils/namesFormater";
import { formatProductTypeUniversal } from "../../utils/productTypeUniversalFormatter";
import { formatHandlerCodeUniversal } from "../../utils/handlerCodeUniversalFormatter";
import { colorProductTypeUniversal, colorHandlerCodeUniversal } from "../../utils/colorEnumFormatter";
import CurrencyTextField from "../../shared/UIParts/InputFields/CurrencyTextField";
import PercentageTextField from "../../shared/UIParts/InputFields/PercentageTextField";
import { manualMimshakDataService } from "../../services/manualMimshakDataService";
import { useCreateManualMimshakData, useUpdateManualMimshakData, useDeleteManualMimshakByIdentifiers, useUpdateManualMimshakIdentifiers } from "../../hooks/useManualMimshaksData";
import { getFieldConfig, getAllManualProductTypes, formatManualProductType, formatSubTypeDisplay } from "../../utils/manualProductTypeFormatter";
import SearchAutocomplete from "../../shared/UIParts/SearchAutocomplete";
import { HandlerAutocompleteField } from "../../shared/Autocompletes/HandlerAutocompleteField";
import { getAllBankHandlerCodes } from "../../utils/bankHandlerCodeFormatter";
import { getAllInvestmentHouseCodesWithBanks } from "../../utils/investmentHouseCodeFormatter";
import LoanCalculator from "../Loan/LoanCalculator";
import { MimshakService } from "../../services/mimshakService";
import MortgageCalculator from "../Mortgage/MortgageCalculator";
import { LoanDetails } from "../../interfaces/DTO/mislaka_xml_models";
import { MortgageDetails } from "../../interfaces/DTO/mortgage/mortgage";
import SelectNewPathDrawer from "../FeesLab/SelectNewPathDrawer";
import { PathFundSelection, replacePathsInProduct } from "../../hooks/feeLab/useNewProductHandlers";
import { MimshakDataUtil } from "../../services/mimshakDataUtil";

interface ManualMimshakProductDrawerProps {
  manualMimshakData: ManualMimshakData | null;
  onClose: () => void;
  contactName?: string;
  reportedPeriod?: string;
  onSave?: () => void;
  initialTab?: number; // טאב התחלתי לפתיחה
}

const ManualMimshakProductDrawer: React.FC<ManualMimshakProductDrawerProps> = ({
  manualMimshakData,
  onClose,
  contactName,
  reportedPeriod,
  onSave,
  initialTab = 0,
}) => {
  const [activeTab, setActiveTab] = useState(initialTab);
  const [isEditing, setIsEditing] = useState(
    !!manualMimshakData && !!reportedPeriod && manualMimshakData.reported_period === reportedPeriod
  );
  const [isEditingIdentifiers, setIsEditingIdentifiers] = useState(false);
  const [editedValues, setEditedValues] = useState({
    funds_sum: manualMimshakData?.funds_sum || 0,
    deposit_amount: manualMimshakData?.deposit_amount || 0,
    withdrawal_amount: manualMimshakData?.withdrawal_amount || 0,
    actualiation_date: manualMimshakData?.actualiation_date || "",
  });
  const [editedIdentifiers, setEditedIdentifiers] = useState({
    policy_number: manualMimshakData?.policy_number || "",
    handler_code: manualMimshakData?.handler_code || "",
    product_type: manualMimshakData?.product_type || 0,
  });
  // מצב מזהים מוצג בכותרת — מתעדכן לאחר שמירה מוצלחת
  const [displayedIdentifiers, setDisplayedIdentifiers] = useState({
    policy_number: manualMimshakData?.policy_number || "",
    handler_code: manualMimshakData?.handler_code || "",
    product_type: manualMimshakData?.product_type || 0,
  });
  const [historicalData, setHistoricalData] = useState<ManualMimshakData[]>([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [pathDrawerOpen, setPathDrawerOpen] = useState(false);
  const [editedPaths, setEditedPaths] = useState<PathFundSelection[]>([]);
  const [editedSavingFee, setEditedSavingFee] = useState<number | null>(null);
  const fundsSumDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const createMutation = useCreateManualMimshakData();
  const updateMutation = useUpdateManualMimshakData();
  const deleteMutation = useDeleteManualMimshakByIdentifiers();
  const updateIdentifiersMutation = useUpdateManualMimshakIdentifiers();

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  // עדכון editedValues כאשר manualMimshakData משתנה
  useEffect(() => {
    if (manualMimshakData) {
      setEditedValues({
        funds_sum: manualMimshakData.funds_sum || 0,
        deposit_amount: manualMimshakData.deposit_amount || 0,
        withdrawal_amount: manualMimshakData.withdrawal_amount || 0,
        actualiation_date: manualMimshakData.actualiation_date || "",
      });
      setEditedIdentifiers({
        policy_number: manualMimshakData.policy_number || "",
        handler_code: manualMimshakData.handler_code || "",
        product_type: manualMimshakData.product_type || 0,
      });
      // טעינת מסלולים קיימים לעריכה
      const existingPaths = manualMimshakData.data?.products?.[0]?.policies_or_accounts?.[0]?.investment_paths;
      const loadedPaths: PathFundSelection[] = existingPaths
        ?.filter(p => !!p.path_code)
        .map(p => ({
          fund: { uid: 0, id: 0, fund_id: p.path_code as number, fund_name: p.path_name ?? "", file_type: 0 as any },
          amount: p.accumulated_amount ?? 0,
          depositPercentage: p.deposit_percentage,
        })) ?? [];
      setEditedPaths(loadedPaths);
      setEditedSavingFee(manualMimshakData.saving_fee ?? null);
      setIsEditing(!!reportedPeriod && manualMimshakData.reported_period === reportedPeriod);
      setDisplayedIdentifiers({
        policy_number: manualMimshakData.policy_number || "",
        handler_code: manualMimshakData.handler_code || "",
        product_type: manualMimshakData.product_type || 0,
      });
      // עדכון טאב אקטיבי אם יש טאב התחלתי
      setActiveTab(initialTab);
    }
  }, [manualMimshakData, initialTab]);

  const loadHistoricalData = async () => {
    if (!manualMimshakData) return;

    setIsLoadingHistory(true);
    try {
      const data = await manualMimshakDataService.getLightFilteredMimshakData(
        manualMimshakData.contact_id,
        manualMimshakData.handler_code,
        undefined, // reported_period - רוצים את כל התקופות
        manualMimshakData.product_type.toString(),
        manualMimshakData.policy_number,
        undefined, // agent_code
        0, // skip
        100 // limit - הגבלה של 100 רשומות היסטוריות
      );

      // מיון לפי תקופה מהחדש לישן
      const sortedData = data.sort((a, b) => {
        return b.reported_period.localeCompare(a.reported_period);
      });

      setHistoricalData(sortedData);
    } catch (error) {
      console.error("Failed to load historical data:", error);
    } finally {
      setIsLoadingHistory(false);
    }
  };

  // טעינת נתונים היסטוריים
  useEffect(() => {
    if (!manualMimshakData || activeTab !== 1) return;
    loadHistoricalData();
  }, [manualMimshakData, activeTab]);

  const handleSave = async () => {
    if (!manualMimshakData || !reportedPeriod) return;

    try {
      const isSamePeriod = manualMimshakData.reported_period === reportedPeriod;

      const updatedData: Partial<ManualMimshakData> = {
        funds_sum: editedValues.funds_sum,
        deposit_amount: editedValues.deposit_amount,
        withdrawal_amount: editedValues.withdrawal_amount,
        actualiation_date: editedValues.actualiation_date || null,
      };

      // דמי ניהול — פוליסת חיסכון או סוג עם savingFeeLabel
      if (isSavingsPolicy || fieldConfig.savingFeeLabel) {
        updatedData.saving_fee = editedSavingFee ?? undefined;
      }

      // מסלולים — פוליסת חיסכון בלבד
      if (isSavingsPolicy) {
        updatedData.fund_ids = MimshakDataUtil.buildSavingsPolicyFundIds(editedPaths);
        updatedData.data = MimshakDataUtil.buildNewPolicyMimshak({
          handlerCode: manualMimshakData.handler_code,
          productType: manualMimshakData.product_type,
          policyNumber: manualMimshakData.policy_number,
          reportedPeriod: manualMimshakData.reported_period ?? reportedPeriod ?? "",
          fundsSum: editedValues.funds_sum ?? 0,
          selectedPaths: editedPaths,
          savingFee: editedSavingFee ?? null,
        }) as any;
      }

      if (isSamePeriod && manualMimshakData.id) {
        // אם התקופה זהה - עדכון מלא של רשומה קיימת (כדי שה-data יעבור סריאליזציה נכונה)
        await updateMutation.mutateAsync({
          id: manualMimshakData.id,
          data: { ...manualMimshakData, ...updatedData },
        });
      } else {
        // אם התקופה שונה - יצירת רשומה חדשה
        const newProduct: ManualMimshakData = {
          ...manualMimshakData,
          ...updatedData,
          reported_period: reportedPeriod,
        };
        await createMutation.mutateAsync(newProduct);
      }

      setEditedPaths([]);
      setIsEditing(false);

      if (onSave) {
        onSave();
      }
      onClose();

      // טעינה מחדש של ההיסטוריה אם אנחנו בטאב ההיסטוריה
      if (activeTab === 1) {
        await loadHistoricalData();
      }
    } catch (error) {
      console.error("Failed to save manual mimshak data:", error);
    }
  };

  const handleSaveIdentifiers = async () => {
    if (!manualMimshakData) return;

    try {
      await updateIdentifiersMutation.mutateAsync({
        contact_id: manualMimshakData.contact_id,
        old_handler_code: manualMimshakData.handler_code,
        old_product_type: manualMimshakData.product_type,
        old_policy_number: manualMimshakData.policy_number,
        new_handler_code: editedIdentifiers.handler_code,
        new_product_type: editedIdentifiers.product_type,
        new_policy_number: editedIdentifiers.policy_number,
      });

      // עדכון הכותרת עם המזהים החדשים
      setDisplayedIdentifiers({
        policy_number: editedIdentifiers.policy_number,
        handler_code: editedIdentifiers.handler_code,
        product_type: editedIdentifiers.product_type,
      });

      // סגירת מצב עריכה
      setIsEditingIdentifiers(false);

      // רענון הנתונים
      if (onSave) {
        onSave();
      }

      // טעינה מחדש של ההיסטוריה
      if (activeTab === 1) {
        await loadHistoricalData();
      }
    } catch (error) {
      console.error("Failed to update identifiers:", error);
    }
  };

  const handleDeleteProduct = async () => {
    if (!manualMimshakData) return;

    try {
      await deleteMutation.mutateAsync({
        contact_id: manualMimshakData.contact_id,
        handler_code: manualMimshakData.handler_code,
        product_type: manualMimshakData.product_type,
        policy_number: manualMimshakData.policy_number,
      });

      // סגירת הדיאלוג והחלון
      setDeleteDialogOpen(false);
      onClose();

      // רענון הנתונים
      if (onSave) {
        onSave();
      }
    } catch (error) {
      console.error("Failed to delete manualMimshakData:", error);
    }
  };

  const handleSaveLoanDetails = async (updatedLoanDetails: LoanDetails) => {
    if (!manualMimshakData || !manualMimshakData.id) return;

    try {
      // עדכון ה-data עם LoanDetails החדש
      const updatedData = {
        ...manualMimshakData.data,
        products: manualMimshakData.data?.products?.map((product, pIndex) =>
          pIndex === 0 ? {
            ...product,
            policies_or_accounts: product.policies_or_accounts?.map((policy, polIndex) =>
              polIndex === 0 ? {
                ...policy,
                loan_details: [updatedLoanDetails]
              } : policy
            )
          } : product
        )
      };

      // שימוש ב-UPDATE ולא ב-PARTIAL UPDATE
      await updateMutation.mutateAsync({
        id: manualMimshakData.id,
        data: {
          ...manualMimshakData,
          data: updatedData
        }
      });

      // רענון הנתונים
      if (onSave) {
        onSave();
      }
    } catch (error) {
      console.error("Failed to save loan details:", error);
      throw error;
    }
  };

  const handleSaveMortgageDetails = async (updatedMortgageDetails: MortgageDetails) => {
    if (!manualMimshakData || !manualMimshakData.id) return;

    try {
      // עדכון ה-data עם MortgageDetails החדש
      const updatedData = {
        ...manualMimshakData.data,
        products: manualMimshakData.data?.products?.map((product, pIndex) =>
          pIndex === 0 ? {
            ...product,
            policies_or_accounts: product.policies_or_accounts?.map((policy, polIndex) =>
              polIndex === 0 ? {
                ...policy,
                mortgage_details: [updatedMortgageDetails]
              } : policy
            )
          } : product
        )
      };

      // שימוש ב-UPDATE ולא ב-PARTIAL UPDATE
      await updateMutation.mutateAsync({
        id: manualMimshakData.id,
        data: {
          ...manualMimshakData,
          data: updatedData
        }
      });

      // רענון הנתונים
      if (onSave) {
        onSave();
      }
    } catch (error) {
      console.error("Failed to save mortgage details:", error);
      throw error;
    }
  };

  // אופציות לסוגי מוצרים - חייב להיות לפני early return
  const productTypeOptions = useMemo(() => getAllManualProductTypes(), []);

  // רשימות מטפלים - חייב להיות לפני early return
  const bankHandlerCodes = useMemo(() => getAllBankHandlerCodes(), []);
  const investmentHouseCodes = useMemo(() => getAllInvestmentHouseCodesWithBanks(), []);

  // קבלת קונפיגורציה של שדות לפי סוג מוצר
  const fieldConfig = manualMimshakData ? getFieldConfig(manualMimshakData.product_type) : getFieldConfig(ManualProductType.OTHER);

  const isBankAccount = manualMimshakData?.product_type === ManualProductType.BANK_ACCOUNT;
  const isInvestmentPortfolio = manualMimshakData?.product_type === ManualProductType.INVESTMENT_PORTFOLIO;
  const isSavingsPolicy = manualMimshakData?.product_type === ManualProductType.PURE_SAVINGS_POLICY;
  const isMortgage = manualMimshakData?.product_type === ManualProductType.MORTGAGE;

  // אופציות ל-Handler - חייב להיות לפני early return
  const handlerOptions = useMemo(() => {
    if (fieldConfig.handlerCodeOptions) {
      return fieldConfig.handlerCodeOptions.map(opt => opt.label);
    }
    const list = (isBankAccount || isMortgage) ? bankHandlerCodes : isInvestmentPortfolio ? investmentHouseCodes : [];
    return list.map(item => item.label);
  }, [fieldConfig, isBankAccount, isMortgage, isInvestmentPortfolio, bankHandlerCodes, investmentHouseCodes]);

  // תרגום מ-label ל-value (Handler)
  const getHandlerValue = (label: string): string => {
    if (fieldConfig.handlerCodeOptions) {
      const found = fieldConfig.handlerCodeOptions.find(opt => opt.label === label);
      return found ? found.value : "";
    }
    const list = (isBankAccount || isMortgage) ? bankHandlerCodes : isInvestmentPortfolio ? investmentHouseCodes : [];
    const found = list.find(item => item.label === label);
    return found ? found.value : "";
  };

  // תרגום מ-value ל-label (Handler)
  const getHandlerLabel = (value: string): string => {
    if (fieldConfig.handlerCodeOptions) {
      const found = fieldConfig.handlerCodeOptions.find(opt => opt.value === value);
      return found ? found.label : "";
    }
    const list = (isBankAccount || isMortgage) ? bankHandlerCodes : isInvestmentPortfolio ? investmentHouseCodes : [];
    const found = list.find(item => item.value === value);
    return found ? found.label : "";
  };

  if (!manualMimshakData) return null;

  const productTypeColor = colorProductTypeUniversal(displayedIdentifiers.product_type);
  const handlerColor = colorHandlerCodeUniversal(displayedIdentifiers.handler_code);

  return (
    <StyledDrawer
      open={!!manualMimshakData}
      onClose={onClose}
      width={isMortgage ? 1000 : 500}
    >
      {/* כותרת עם פרטי המוצר */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 3, pb: 2, borderBottom: "1px solid #e0e0e0" }}>
        {/* סוג מוצר */}
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Typography variant="body2" color="text.secondary" sx={{ minWidth: 120 }}>
            סוג מוצר:
          </Typography>
          <Chip
            label={formatProductTypeUniversal(displayedIdentifiers.product_type)}
            sx={{ backgroundColor: productTypeColor, color: "white", fontSize: "1rem", height: 32 }}
          />
        </Box>

        {/* מספר פוליסה */}
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Typography variant="body2" color="text.secondary" sx={{ minWidth: 120 }}>
            {fieldConfig.policyNumberLabel}:
          </Typography>
          <Typography variant="body1" fontWeight="bold">
            {formatSubTypeDisplay(manualMimshakData?.adjusted_product_type, displayedIdentifiers.product_type as ManualProductType, displayedIdentifiers.policy_number) || displayedIdentifiers.policy_number}
          </Typography>
        </Box>

        {/* יצרן */}
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Typography variant="body2" color="text.secondary" sx={{ minWidth: 120 }}>
            {fieldConfig.handlerCodeLabel}:
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box
              sx={{
                width: 10,
                height: 30,
                borderRadius: "20%",
                backgroundColor: handlerColor,
              }}
            />
            <Typography variant="body1">
              {formatHandlerCodeUniversal(displayedIdentifiers.handler_code)}
            </Typography>
          </Box>
        </Box>

        {/* שם לקוח */}
        {contactName && (
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Typography variant="body2" color="text.secondary" sx={{ minWidth: 120 }}>
              לקוח:
            </Typography>
            <Typography variant="body1">{contactName}</Typography>
          </Box>
        )}

        {/* כפתור עריכת פרטים + מחק */}
        {!isEditingIdentifiers && (
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Button
              size="small"
              variant="outlined"
              color="primary"
              startIcon={<EditIcon fontSize="small" />}
              onClick={() => setIsEditingIdentifiers(true)}
            >
              ערוך פרטים
            </Button>
            <Button
              size="small"
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon fontSize="small" sx={{ ml: 1 }} />}
              onClick={() => setDeleteDialogOpen(true)}
            >
              מחק מוצר
            </Button>
          </Box>
        )}

        {/* מצב עריכה של שדות מזהים */}
        {isEditingIdentifiers && (
          <Box sx={{
            p: 2,
            borderRadius: 1,
            border: "1px solid #e0e0e0",
            display: "flex",
            flexDirection: "column",
            gap: 2
          }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <Typography variant="subtitle2" fontWeight="bold" color="warning.main">
                עריכת פרטי מוצר
              </Typography>
              <IconButton
                size="small"
                onClick={() => {
                  setIsEditingIdentifiers(false);
                  setEditedIdentifiers({
                    policy_number: manualMimshakData.policy_number || "",
                    handler_code: manualMimshakData.handler_code || "",
                    product_type: manualMimshakData.product_type || 0,
                  });
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>

            {/* סוג מוצר */}
            <Box>
              <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>
                סוג מוצר *
              </Typography>
              <SearchAutocomplete
                placeholder="בחר סוג מוצר..."
                options={productTypeOptions.map(opt => opt.label)}
                value={productTypeOptions.find(opt => opt.value === editedIdentifiers.product_type)?.label || ""}
                onChange={(selectedLabel) => {
                  const option = productTypeOptions.find(opt => opt.label === selectedLabel);
                  setEditedIdentifiers({
                    ...editedIdentifiers,
                    product_type: option?.value || 0
                  });
                }}
                showIcons={false}
              />
            </Box>

            {/* יצרן */}
            {isSavingsPolicy ? (
              <Box>
                <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>
                  {fieldConfig.handlerCodeLabel} *
                </Typography>
                <HandlerAutocompleteField
                  value={editedIdentifiers.handler_code}
                  onChange={(value) => setEditedIdentifiers({ ...editedIdentifiers, handler_code: value })}
                  placeholder={`בחר ${fieldConfig.handlerCodeLabel}...`}
                  showIcons={true}
                  freeSolo={fieldConfig.handlerCodeFreeSolo}
                />
              </Box>
            ) : fieldConfig.handlerCodeOptions || isBankAccount || isMortgage || isInvestmentPortfolio ? (
              <Box>
                <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>
                  {fieldConfig.handlerCodeLabel} *
                </Typography>
                <SearchAutocomplete
                  placeholder={`בחר ${fieldConfig.handlerCodeLabel}...`}
                  options={handlerOptions}
                  value={getHandlerLabel(editedIdentifiers.handler_code)}
                  onChange={(selectedLabel) => {
                    const value = selectedLabel ? getHandlerValue(selectedLabel) : "";
                    setEditedIdentifiers({ ...editedIdentifiers, handler_code: value });
                  }}
                  showIcons={!fieldConfig.handlerCodeOptions}
                />
              </Box>
            ) : (
              <TextField
                fullWidth
                label={fieldConfig.handlerCodeLabel}
                value={editedIdentifiers.handler_code}
                onChange={(e) =>
                  setEditedIdentifiers({ ...editedIdentifiers, handler_code: e.target.value })
                }
                required
              />
            )}

            {/* מספר פוליסה */}
            <TextField
              label={fieldConfig.policyNumberLabel}
              value={editedIdentifiers.policy_number}
              onChange={(e) =>
                setEditedIdentifiers({ ...editedIdentifiers, policy_number: e.target.value })
              }
              required
              sx={{ maxWidth: 200 }}
            />

            {/* כפתורים */}
            <Box sx={{ mt: 1 }}>
              <Button
                variant="contained"
                color="secondary"
                startIcon={<SaveIcon sx={{ ml: 1 }} />}
                onClick={handleSaveIdentifiers}
                disabled={updateIdentifiersMutation.isPending}
              >
                {updateIdentifiersMutation.isPending ? "שומר..." : "שמור שינויים"}
              </Button>
            </Box>
          </Box>
        )}
      </Box>
      <Tabs value={activeTab} onChange={handleTabChange} sx={{ mb: 2 }}>
        <Tab label="סיכום" />
        <Tab label="היסטוריה" />
        {manualMimshakData?.product_type === ManualProductType.LOAN && <Tab label="פרטי הלוואה" />}
        {manualMimshakData?.product_type === ManualProductType.MORTGAGE && <Tab label="פרטי משכנתא" />}
      </Tabs>

      {/* טאב ראשון - סיכום */}
      {activeTab === 0 && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          {/* יתרה — read only או עריכה */}
          <Box sx={{ backgroundColor: "#f5f5f5", borderRadius: 2, px: 3, py: 2, textAlign: "right" }}>
            {/* שורת נכון לתקופה + כפתור עדכן */}
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
              <Typography variant="body1" color="text.secondary">
                נכון לתקופה: <strong>{manualMimshakData.reported_period}</strong>
              </Typography>
              {!isEditing && (
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  onClick={() => setIsEditing(true)}
                >
                  עדכן נתונים ל {reportedPeriod || manualMimshakData.reported_period}
                </Button>
              )}
            </Box>

            {/* שדות — read only או עריכה */}
            {!isEditing ? (
              <>
                <Typography variant="body2" color="text.secondary">
                  {fieldConfig.fundsSumLabel}: <Typography component="span" variant="h4" fontWeight="bold"><span dir="ltr">{formatCurrency(editedValues.funds_sum)}</span></Typography>
                </Typography>
                {fieldConfig.depositAmountLabel && (editedValues.deposit_amount ?? 0) !== 0 && (
                  <Typography variant="body2" color="text.secondary">
                    {fieldConfig.depositAmountLabel}: <Typography component="span" variant="body1" fontWeight="bold"><span dir="ltr">{formatCurrency(editedValues.deposit_amount)}</span></Typography>
                  </Typography>
                )}
                {fieldConfig.withdrawalAmountLabel && (editedValues.withdrawal_amount ?? 0) !== 0 && (
                  <Typography variant="body2" color="text.secondary">
                    {fieldConfig.withdrawalAmountLabel}: <Typography component="span" variant="body1" fontWeight="bold"><span dir="ltr">{formatCurrency(editedValues.withdrawal_amount)}</span></Typography>
                  </Typography>
                )}
                {fieldConfig.actualiationDateLabel && editedValues.actualiation_date && (
                  <Typography variant="body2" color="text.secondary">
                    {fieldConfig.actualiationDateLabel}: <Typography component="span" variant="body1" fontWeight="bold">{editedValues.actualiation_date}</Typography>
                  </Typography>
                )}
              </>
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <CurrencyTextField
                  label={fieldConfig.fundsSumLabel}
                  value={editedValues.funds_sum}
                  onChange={(value) => {
                    const newSum = value ?? 0;
                    setEditedValues(prev => ({ ...prev, funds_sum: newSum }));
                    if (fundsSumDebounceRef.current) clearTimeout(fundsSumDebounceRef.current);
                    fundsSumDebounceRef.current = setTimeout(() => {
                      setEditedPaths(prev => {
                        if (prev.length === 0) return prev;
                        const oldTotal = prev.reduce((s, p) => s + p.amount, 0);
                        const scaled = prev.map(p => ({
                          ...p,
                          amount: oldTotal > 0 ? (p.amount / oldTotal) * newSum : newSum / prev.length,
                        }));
                        return scaled;
                      });
                    }, 800);
                  }}
                  fullWidth
                />
                {fieldConfig.depositAmountLabel && (
                  <CurrencyTextField
                    label={fieldConfig.depositAmountLabel}
                    value={editedValues.deposit_amount}
                    onChange={(value) => setEditedValues({ ...editedValues, deposit_amount: value ?? 0 })}
                    fullWidth
                  />
                )}
                {fieldConfig.withdrawalAmountLabel && (
                  <CurrencyTextField
                    label={fieldConfig.withdrawalAmountLabel}
                    value={editedValues.withdrawal_amount}
                    onChange={(value) => setEditedValues({ ...editedValues, withdrawal_amount: value ?? 0 })}
                    fullWidth
                  />
                )}
                {fieldConfig.actualiationDateLabel && (
                  <TextField
                    label={fieldConfig.actualiationDateLabel}
                    type="date"
                    value={editedValues.actualiation_date || ""}
                    onChange={(e) => setEditedValues({ ...editedValues, actualiation_date: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />
                )}

                {/* דמי ניהול מצבירה — כל סוג עם savingFeeLabel */}
                {fieldConfig.savingFeeLabel && (
                  <PercentageTextField
                    label={fieldConfig.savingFeeLabel}
                    value={editedSavingFee ?? 0}
                    onChange={(val) => setEditedSavingFee(val)}
                    decimalPlaces={2}
                    max={10}
                    sx={{ maxWidth: 200 }}
                  />
                )}

                {/* עריכת מסלולי השקעה — פוליסת חיסכון */}
                {isSavingsPolicy && (
                  <>
                    <Box>
                      <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>
                        מסלולי השקעה:
                      </Typography>
                      {editedPaths.length === 0 && (
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => setPathDrawerOpen(true)}
                        >
                          בחר מסלול...
                        </Button>
                      )}
                      {editedPaths.length > 0 && (
                        <>
                          <Stack spacing={0.5} sx={{ mb: 1 }}>
                            {editedPaths.map((p, i) => (
                              <Typography key={i} variant="body2" fontWeight="bold">
                                {p.fund.fund_name} ({p.fund.fund_id}) | {p.amount.toLocaleString()} ₪
                              </Typography>
                            ))}
                          </Stack>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => setPathDrawerOpen(true)}
                          >
                            שנה מסלול...
                          </Button>
                        </>
                      )}
                    </Box>
                  </>
                )}

                {/* כפתורי שמור/ביטול */}
                {(() => {
                  const pathsTotal = editedPaths.reduce((s, p) => s + p.amount, 0);
                  const pathsInvalid = isSavingsPolicy && editedPaths.length > 0 &&
                    editedValues.funds_sum > 0 &&
                    Math.abs(pathsTotal - editedValues.funds_sum) > 1;
                  return (
                    <>
                      {pathsInvalid && (
                        <Typography variant="body2" color="error">
                          סכום המסלולים ({pathsTotal.toLocaleString()} ₪) אינו תואם את היתרה הכוללת ({editedValues.funds_sum.toLocaleString()} ₪)
                        </Typography>
                      )}
                      <Box sx={{ display: "flex", gap: 2 }}>
                        <Button
                          variant="contained"
                          color="primary"
                          startIcon={<SaveIcon sx={{ ml: 1 }} />}
                          onClick={handleSave}
                          disabled={pathsInvalid}
                        >
                          שמור
                        </Button>
                        <Button
                          variant="outlined"
                          onClick={() => {
                            setIsEditing(false);
                            setEditedValues({
                              funds_sum: manualMimshakData.funds_sum || 0,
                              deposit_amount: manualMimshakData.deposit_amount || 0,
                              withdrawal_amount: manualMimshakData.withdrawal_amount || 0,
                              actualiation_date: manualMimshakData.actualiation_date || "",
                            });
                            setEditedPaths([]);
                          }}
                        >
                          ביטול
                        </Button>
                      </Box>
                    </>
                  );
                })()}
              </Box>
            )}
          </Box>

          {/* מסלולים קיימים — פוליסת חיסכון, read only */}
          {!isEditing && isSavingsPolicy && (() => {
            const paths = manualMimshakData.data?.products?.[0]?.policies_or_accounts?.[0]?.investment_paths;
            if (!paths || paths.length === 0) return null;
            return (
              <Box sx={{ backgroundColor: "#f5f5f5", borderRadius: 2, px: 3, py: 2, textAlign: "right" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                    מסלולי השקעה:
                  </Typography>
                  {paths.length === 1 && !paths[0].path_code && (
                    <Typography variant="body2" color="error">*לא נבחר מסלול</Typography>
                  )}
                </Box>
                {paths.length === 1 && !paths[0].path_code ? null : (
                  paths.map((p, i) => (
                    <Typography key={i} variant="body2" color="text.secondary">
                      {p.path_name} ({p.path_code}): <Typography component="span" variant="body1" fontWeight="bold"><span dir="ltr">{(p.accumulated_amount ?? 0).toLocaleString()} ₪</span></Typography>
                    </Typography>
                  ))
                )}
              </Box>
            );
          })()}

        </Box>
      )}

      {/* טאב שני - היסטוריה */}
      {activeTab === 1 && (
        <Box>
          {isLoadingHistory ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : historicalData.length === 0 ? (
            <Typography variant="body1" color="text.secondary" sx={{ textAlign: "center", py: 4 }}>
              לא נמצאו נתונים היסטוריים
            </Typography>
          ) : (
            <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell align="center" sx={{ fontWeight: "bold" }}>תקופה</TableCell>
                    <TableCell align="center" sx={{ fontWeight: "bold" }}>{fieldConfig.fundsSumLabel}</TableCell>
                    {fieldConfig.depositAmountLabel && (
                      <TableCell align="center" sx={{ fontWeight: "bold" }}>{fieldConfig.depositAmountLabel}</TableCell>
                    )}
                    {fieldConfig.withdrawalAmountLabel && (
                      <TableCell align="center" sx={{ fontWeight: "bold" }}>{fieldConfig.withdrawalAmountLabel}</TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {historicalData.map((row, index) => (
                    <TableRow
                      key={index}
                      sx={{
                        '&:nth-of-type(odd)': { backgroundColor: "#f5f5f5" },
                        '&:hover': { backgroundColor: "#e0e0e0" }
                      }}
                    >
                      <TableCell align="center">{row.reported_period}</TableCell>
                      <TableCell align="center">
                        <span dir="ltr">{formatCurrency(row.funds_sum || 0)}</span>
                      </TableCell>
                      {fieldConfig.depositAmountLabel && (
                        <TableCell align="center">
                          <span dir="ltr">{formatCurrency(row.deposit_amount || 0)}</span>
                        </TableCell>
                      )}
                      {fieldConfig.withdrawalAmountLabel && (
                        <TableCell align="center">
                          <span dir="ltr">{formatCurrency(row.withdrawal_amount || 0)}</span>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}

      {/* טאב שלישי - מחשבון הלוואה */}
      {activeTab === 2 && manualMimshakData?.product_type === ManualProductType.LOAN && (
        <Box>
          {MimshakService.getAllLoansFromMimshak(manualMimshakData.data).map((loanDetails, index) => (
            <LoanCalculator
              key={index}
              loanDetails={loanDetails}
              autoCalculate={true}
              reportedPeriod={reportedPeriod}
              mode="edit"
              onSave={handleSaveLoanDetails}
            />
          ))}
        </Box>
      )}

      {/* טאב שלישי/רביעי - מחשבון משכנתא */}
      {activeTab === 2 && manualMimshakData?.product_type === ManualProductType.MORTGAGE && (
        <Box>
          {(() => {
            // חילוץ פרטי משכנתא מתוך הנתונים - cast ל-any כי mortgage_details לא מוגדר ב-PolicyOrAccount
            const mortgageDetails = (manualMimshakData.data?.products?.[0]?.policies_or_accounts?.[0] as any)?.mortgage_details?.[0];

            return (
              <MortgageCalculator
                mortgageDetails={mortgageDetails}
                mode="edit"
                onSave={handleSaveMortgageDetails}
                reportedPeriod={reportedPeriod}
              />
            );
          })()}
        </Box>
      )}

      {/* בחירת מסלול — פוליסת חיסכון */}
      {isSavingsPolicy && (
        <SelectNewPathDrawer
          open={pathDrawerOpen}
          onClose={() => setPathDrawerOpen(false)}
          mimshak={(() => {
            const base = { ...manualMimshakData, funds_sum: editedValues.funds_sum } as any;
            const result = editedPaths.length > 0 ? replacePathsInProduct(base, manualMimshakData.policy_number, editedPaths) : base;
            return result;
          })()}
          onFundsSelected={(selections) => {
            setEditedPaths(selections);
            setPathDrawerOpen(false);
          }}
        />
      )}

      {/* דיאלוג אישור מחיקה */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        sx={{ direction: "rtl" }}
      >
        <DialogTitle>מחיקת מוצר</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {historicalData.length > 1 ? (
              <>
                האם למחוק את המוצר וכל ההיסטוריה שלו ({historicalData.length} רשומות)?
                <br />
                <strong>פעולה זו אינה ניתנת לביטול!</strong>
              </>
            ) : (
              <>
                האם למחוק את המוצר?
                <br />
                <strong>פעולה זו אינה ניתנת לביטול!</strong>
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} color="primary">
            ביטול
          </Button>
          <Button
            onClick={handleDeleteProduct}
            color="error"
            variant="contained"
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending ? "מוחק..." : "מחק"}
          </Button>
        </DialogActions>
      </Dialog>
    </StyledDrawer>
  );
};

export default ManualMimshakProductDrawer;
